import { keepPreviousData, queryOptions } from '@tanstack/react-query';
import { apiClient } from '@/api/axios';
import { API } from '@/api/endpoints';
import { STALE_TIME } from '@/api/staleTimes';
import { downloadApiFile } from '@/utils/download';
import {
  pcmpPendingReportSchema,
  pcmpPerformanceReportSchema,
  pcmpReportFilterOptionsSchema,
  type PcmpPendingReport,
  type PcmpPendingReportParams,
  type PcmpPerformanceReport,
  type PcmpReportFilters,
  type PcmpReportFilterOptions,
} from '../types/pcmpReports.types';

export const pcmpReportQueryKeys = {
  all: () => ['pcmp-reports'] as const,
  filterOptions: () => [...pcmpReportQueryKeys.all(), 'filter-options'] as const,
  pending: (params: PcmpPendingReportParams) =>
    [...pcmpReportQueryKeys.all(), 'pending', params] as const,
  performance: (params: PcmpReportFilters) =>
    [...pcmpReportQueryKeys.all(), 'performance', params] as const,
};

export const pcmpReportFilterOptionsQueryOptions = () =>
  queryOptions({
    queryKey: pcmpReportQueryKeys.filterOptions(),
    queryFn: async (): Promise<PcmpReportFilterOptions> => {
      const res = await apiClient.get(API.pcmpReport.filterOptions);
      return pcmpReportFilterOptionsSchema.parse(res.data);
    },
    staleTime: STALE_TIME,
  });

export const pcmpPendingReportQueryOptions = (params: PcmpPendingReportParams) =>
  queryOptions({
    queryKey: pcmpReportQueryKeys.pending(params),
    queryFn: async (): Promise<PcmpPendingReport> => {
      const res = await apiClient.get(API.pcmpReport.pending, { params });
      return pcmpPendingReportSchema.parse(res.data);
    },
    placeholderData: keepPreviousData,
    staleTime: STALE_TIME,
  });

/** The API names the file the same way; its header is not readable cross-origin. */
export function pcmpPendingReportXlsxFilename(date: string): string {
  return `Division_Region_Pending_Report_${date}.xlsx`;
}

export function downloadPcmpPendingReportXlsx(
  params: PcmpPendingReportParams,
  date: string,
): Promise<void> {
  return downloadApiFile(API.pcmpReport.pendingXlsx, pcmpPendingReportXlsxFilename(date), {
    ...params,
  });
}

export const pcmpPerformanceReportQueryOptions = (params: PcmpReportFilters) =>
  queryOptions({
    queryKey: pcmpReportQueryKeys.performance(params),
    queryFn: async (): Promise<PcmpPerformanceReport> => {
      const res = await apiClient.get(API.pcmpReport.performance, { params });
      return pcmpPerformanceReportSchema.parse(res.data);
    },
    placeholderData: keepPreviousData,
    staleTime: STALE_TIME,
  });

/** The API names the file the same way; its header is not readable cross-origin. */
export function pcmpPerformanceReportXlsxFilename(date: string): string {
  return `PCMP_Performance_Report_${date}.xlsx`;
}

export function downloadPcmpPerformanceReportXlsx(
  params: PcmpReportFilters,
  date: string,
): Promise<void> {
  return downloadApiFile(API.pcmpReport.performanceXlsx, pcmpPerformanceReportXlsxFilename(date), {
    ...params,
  });
}
